import {
    getAPIGatewayDataFromParent,
    getSortPathGeneratorAPIGatewayRequest
} from "../handler/ParentDataHandler";

export const GET_PATH_SCHEDULE_ENDPOINT = '/spg/pathschedule/get';
export const SET_PATH_SCHEDULE_ENDPOINT = '/spg/pathschedule/set';
export const DELETE_PATH_SCHEDULE_ENDPOINT = '/spg/pathschedule/delete';
export const GET_SORT_PATH_INFO_LIST_ENDPOINT = '/spg/sortpath/info/list';

/**
 * Fetches the active and staged path schedules for a station + cycle.
 *
 * GetPathSchedule is scoped to a single station+cycle combination. The backend
 * request field is `cycleId`, but its value is the cycle *name* (e.g. "CYCLE_1"),
 * because the PathSchedule partition key is {stationCode}_{cycleId} and the backend,
 * SortPathSetting/SortPathInfo, and reservation/constraints flows all identify a cycle
 * by name (not the cycle UUID). Each cycle's schedules must be fetched independently.
 * An optional date range (fromDate/toDate) filters the staged entries server-side.
 *
 * @param nodeId station code
 * @param cycleId cycle identifier (the cycle *name*, e.g. "CYCLE_1") whose schedules to retrieve
 * @param fromDate optional inclusive lower bound (date-only or date-time string)
 * @param toDate optional inclusive upper bound (date-only or date-time string)
 * @returns {Promise<*>} GetPathScheduleOutput { activeSchedule, stagedSchedules } or errorResponse
 */
export async function getPathSchedule(nodeId, cycleId, fromDate, toDate) {
    const requestBody = {
        stationCode: nodeId,
        cycleId: cycleId,
        fromDate: fromDate,
        toDate: toDate
    };

    const request = getSortPathGeneratorAPIGatewayRequest(GET_PATH_SCHEDULE_ENDPOINT, requestBody);
    const response = await getAPIGatewayDataFromParent(request);
    return response ? (response.data || response.errorResponse) : response;
}

/**
 * Creates or overwrites a path schedule entry for a station+cycle+effectiveDate.
 *
 * SetPathSchedule has upsert semantics: an entry at the same {stationCode, cycleId, effectiveDate}
 * is overwritten. Pass the current version to enforce optimistic locking on an update; omit it
 * (undefined/null) to create a new entry.
 *
 * @param nodeId station code
 * @param cycleId cycle identifier (cycle name, matching how entries are keyed)
 * @param effectiveDate date-only ("YYYY-MM-DD") or date-time string
 * @param defaultPathConfig { fillPathId, pickPathId }
 * @param dayOfWeekOverrides optional map of DAY -> { fillPathId, pickPathId }
 * @param version optional current version for optimistic-locked overwrite; omit for new entry
 * @returns {Promise<*>} SetPathScheduleOutput { pathSchedule } or errorResponse
 */
export async function setPathSchedule(nodeId, cycleId, effectiveDate, defaultPathConfig, dayOfWeekOverrides, version) {
    const requestBody = {
        stationCode: nodeId,
        cycleId: cycleId,
        effectiveDate: effectiveDate,
        defaultPathConfig: defaultPathConfig,
        dayOfWeekOverrides: dayOfWeekOverrides,
        version: version
    };

    const request = getSortPathGeneratorAPIGatewayRequest(SET_PATH_SCHEDULE_ENDPOINT, requestBody);
    const response = await getAPIGatewayDataFromParent(request);
    return response ? (response.data || response.errorResponse) : response;
}

/**
 * Deletes a staged (future) path schedule entry. The backend rejects deletion of the
 * currently active entry. Version is required for optimistic locking.
 *
 * @param nodeId station code
 * @param cycleId cycle identifier (cycle name)
 * @param effectiveDate effective date of the entry to delete
 * @param version current version of the entry
 * @returns {Promise<*>} DeletePathScheduleOutput { message } or errorResponse
 */
export async function deletePathSchedule(nodeId, cycleId, effectiveDate, version) {
    const requestBody = {
        stationCode: nodeId,
        cycleId: cycleId,
        effectiveDate: effectiveDate,
        version: version
    };

    const request = getSortPathGeneratorAPIGatewayRequest(DELETE_PATH_SCHEDULE_ENDPOINT, requestBody);
    const response = await getAPIGatewayDataFromParent(request);
    return response ? (response.data || response.errorResponse) : response;
}

/**
 * Fetches the merged list of sort paths (system-generated SPG + optionally NARC manual paths)
 * for a station. Used to populate the Fill/Pick path selectors and to resolve a selected
 * configuration's paired FILL/PICK paths by matching settingId + cycle + type.
 *
 * @param nodeId station code
 * @param includeNarcPaths when true, also returns manually uploaded NARC paths (source="NARC")
 * @returns {Promise<*>} GetSortPathInfoListOutput { sortPathInfoList } or errorResponse
 */
export async function getSortPathInfoList(nodeId, includeNarcPaths) {
    const requestBody = {
        stationCode: nodeId,
        includeNarcPaths: includeNarcPaths
    };

    const request = getSortPathGeneratorAPIGatewayRequest(GET_SORT_PATH_INFO_LIST_ENDPOINT, requestBody);
    const response = await getAPIGatewayDataFromParent(request);
    return response ? (response.data || response.errorResponse) : response;
}
